/**
 * Reference Data Store
 *
 * Manages lookup/reference data from the patient API:
 * allergens, allergy severities, facilities (locations), care units, and beds.
 *
 * When VITE_ENABLE_MOCK === 'true', all load actions return the MOCK_* constants
 * defined in this file instead of making network calls.
 */
import { defineStore } from 'pinia';
import { config } from '@/core/config/app.config';
import { handleSilent404, getApiErrorMessage } from '@/core/utils/api-error.util';
import {
  fetchAllergens,
  fetchAllergySeverities,
  fetchLocations,
  fetchCareUnits,
  fetchBeds,
  fetchPatients,
} from '@/modules/patients/services/patients.api';
import type {
  ApiAllergen,
  ApiAllergySeverity,
  ApiFacility,
  ApiCareUnit,
  ApiBed,
  ApiPatientListItem,
} from '@/modules/patients/types';

// ---------------------------------- Mock reference data ------------------
const now = new Date().toISOString();

const MOCK_ALLERGENS: ApiAllergen[] = [
  { allergen_id: 'alg-001', code: 'PEN', name: 'Penicillin', description: 'Penicillin antibiotics', is_eu_14: false, snomed_code: '91936005', display_order: 1, is_active: true, created_at: now },
  { allergen_id: 'alg-002', code: 'PNT', name: 'Peanuts', description: 'Peanut allergy', is_eu_14: true, snomed_code: '91935009', display_order: 2, is_active: true, created_at: now },
  { allergen_id: 'alg-003', code: 'LTX', name: 'Latex', description: 'Natural rubber latex', is_eu_14: false, snomed_code: '111088007', display_order: 3, is_active: true, created_at: now },
  { allergen_id: 'alg-004', code: 'SHF', name: 'Shellfish', description: 'Shellfish allergy', is_eu_14: true, snomed_code: '91934008', display_order: 4, is_active: true, created_at: now },
  { allergen_id: 'alg-005', code: 'MLK', name: 'Cow Milk', description: "Cow's milk protein", is_eu_14: true, snomed_code: '782555009', display_order: 5, is_active: true, created_at: now },
  { allergen_id: 'alg-006', code: 'SOY', name: 'Soy', description: 'Soy allergy', is_eu_14: true, snomed_code: '91930004', display_order: 6, is_active: true, created_at: now },
];

const MOCK_SEVERITIES: ApiAllergySeverity[] = [
  { severity_id: 'sev-001', hl7_code: 'MI', display_name: 'Mild', description: 'Mild reaction', fhir_criticality: 'low', alert_level: 'INFO', display_order: 1, created_at: now },
  { severity_id: 'sev-002', hl7_code: 'MO', display_name: 'Moderate', description: 'Moderate reaction', fhir_criticality: 'high', alert_level: 'WARNING', display_order: 2, created_at: now },
  { severity_id: 'sev-003', hl7_code: 'SV', display_name: 'Severe', description: 'Severe / life-threatening', fhir_criticality: 'high', alert_level: 'BLOCKING', display_order: 3, created_at: now },
];

const MOCK_HOSPITAL_ID = 'hosp-mock-001';
const MOCK_TENANT = 'tenant-mock-001';
const MOCK_ENTERPRISE = 'ent-mock-001';

const MOCK_FACILITIES: ApiFacility[] = [
  { facility_id: 'fac-001', hospital_id: MOCK_HOSPITAL_ID, enterprise_id: MOCK_ENTERPRISE, tenant_id: MOCK_TENANT, facility_code: 'EW', facility_name: 'East Wing', facility_type: 'NICU', floor_level: '1', is_active: true, created_at: now, updated_at: now },
  { facility_id: 'fac-002', hospital_id: MOCK_HOSPITAL_ID, enterprise_id: MOCK_ENTERPRISE, tenant_id: MOCK_TENANT, facility_code: 'WW', facility_name: 'West Wing', facility_type: 'NICU', floor_level: '1', is_active: true, created_at: now, updated_at: now },
  { facility_id: 'fac-003', hospital_id: MOCK_HOSPITAL_ID, enterprise_id: MOCK_ENTERPRISE, tenant_id: MOCK_TENANT, facility_code: 'NB', facility_name: 'North Block', facility_type: 'GENERAL', floor_level: '2', is_active: true, created_at: now, updated_at: now },
  { facility_id: 'fac-004', hospital_id: MOCK_HOSPITAL_ID, enterprise_id: MOCK_ENTERPRISE, tenant_id: MOCK_TENANT, facility_code: 'SB', facility_name: 'South Block', facility_type: 'GENERAL', floor_level: '2', is_active: true, created_at: now, updated_at: now },
];

const MOCK_CARE_UNITS: Record<string, ApiCareUnit[]> = {
  'fac-001': [
    { care_unit_id: 'cu-001', facility_id: 'fac-001', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, unit_code: 'ICU-A', unit_name: 'ICU', unit_type: 'ICU', bed_capacity: 20, is_active: true, created_at: now, updated_at: now },
    { care_unit_id: 'cu-002', facility_id: 'fac-001', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, unit_code: 'NICU-A', unit_name: 'NICU', unit_type: 'NICU', bed_capacity: 15, is_active: true, created_at: now, updated_at: now },
  ],
  'fac-002': [
    { care_unit_id: 'cu-003', facility_id: 'fac-002', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, unit_code: 'GW-A', unit_name: 'General Ward', unit_type: 'GENERAL', bed_capacity: 30, is_active: true, created_at: now, updated_at: now },
    { care_unit_id: 'cu-004', facility_id: 'fac-002', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, unit_code: 'PED-A', unit_name: 'Pediatrics', unit_type: 'PEDIATRICS', bed_capacity: 25, is_active: true, created_at: now, updated_at: now },
  ],
  'fac-003': [
    { care_unit_id: 'cu-005', facility_id: 'fac-003', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, unit_code: 'MED-A', unit_name: 'Medical Ward', unit_type: 'GENERAL', bed_capacity: 20, is_active: true, created_at: now, updated_at: now },
  ],
  'fac-004': [
    { care_unit_id: 'cu-006', facility_id: 'fac-004', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, unit_code: 'SUR-A', unit_name: 'Surgical Ward', unit_type: 'SURGICAL', bed_capacity: 18, is_active: true, created_at: now, updated_at: now },
  ],
};

const MOCK_BEDS: Record<string, ApiBed[]> = {
  'cu-001': [
    { bed_id: 'bed-001', care_unit_id: 'cu-001', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '101', bed_type: 'STANDARD', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
    { bed_id: 'bed-002', care_unit_id: 'cu-001', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '102', bed_type: 'STANDARD', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
    { bed_id: 'bed-003', care_unit_id: 'cu-001', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '103', bed_type: 'ISOLETTE', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
  ],
  'cu-002': [
    { bed_id: 'bed-004', care_unit_id: 'cu-002', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '201', bed_type: 'INCUBATOR', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
    { bed_id: 'bed-005', care_unit_id: 'cu-002', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '202', bed_type: 'INCUBATOR', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
  ],
  'cu-003': [
    { bed_id: 'bed-006', care_unit_id: 'cu-003', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '301', bed_type: 'STANDARD', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
    { bed_id: 'bed-007', care_unit_id: 'cu-003', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '302', bed_type: 'STANDARD', bed_status: 'OCCUPIED', is_active: true, created_at: now, updated_at: now },
  ],
  'cu-004': [
    { bed_id: 'bed-008', care_unit_id: 'cu-004', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '401', bed_type: 'CRIB', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
    { bed_id: 'bed-009', care_unit_id: 'cu-004', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '402', bed_type: 'CRIB', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
  ],
  'cu-005': [
    { bed_id: 'bed-010', care_unit_id: 'cu-005', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '501', bed_type: 'STANDARD', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
  ],
  'cu-006': [
    { bed_id: 'bed-011', care_unit_id: 'cu-006', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '601', bed_type: 'STANDARD', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
    { bed_id: 'bed-012', care_unit_id: 'cu-006', hospital_id: MOCK_HOSPITAL_ID, tenant_id: MOCK_TENANT, bed_number: '602', bed_type: 'STANDARD', bed_status: 'AVAILABLE', is_active: true, created_at: now, updated_at: now },
  ],
};

interface ReferenceState {
  allergens: ApiAllergen[];
  allergySeverities: ApiAllergySeverity[];
  facilities: ApiFacility[];
  careUnits: ApiCareUnit[];
  beds: ApiBed[];
  patients: ApiPatientListItem[];
  isLoadingAllergens: boolean;
  isLoadingAllergySeverities: boolean;
  isLoadingFacilities: boolean;
  isLoadingCareUnits: boolean;
  isLoadingBeds: boolean;
  isLoadingPatients: boolean;
  error: string | null;
  /** Accumulated map: care_unit_id -> unit_name (persists across facility switches). */
  careUnitNameMap: Record<string, string>;
  /** Accumulated map: bed_id -> bed_number (persists across unit switches). */
  bedLabelMap: Record<string, string>;
}

export const useReferenceStore = defineStore('reference', {
  state: (): ReferenceState => ({
    allergens: [],
    allergySeverities: [],
    facilities: [],
    careUnits: [],
    beds: [],
    patients: [],
    isLoadingAllergens: false,
    isLoadingAllergySeverities: false,
    isLoadingFacilities: false,
    isLoadingCareUnits: false,
    isLoadingBeds: false,
    isLoadingPatients: false,
    error: null,
    careUnitNameMap: {},
    bedLabelMap: {},
  }),

  getters: {
    /** Allergen options formatted for dropdown use. */
    allergenOptions: (state) =>
      (state.allergens ?? []).map((a) => ({
        label: a.name,
        value: a.allergen_id,
        code: a.code,
      })),

    /** Severity options formatted for dropdown use. */
    severityOptions: (state) =>
      state.allergySeverities.map((s) => ({
        label: s.display_name,
        value: s.severity_id,
        hl7Code: s.hl7_code,
      })),

    /** Facility options formatted for dropdown use. */
    facilityOptions: (state) =>
      state.facilities.map((f) => ({
        label: f.facility_name,
        value: f.facility_id,
        code: f.facility_code,
      })),

    /** Care unit options formatted for dropdown use. */
    careUnitOptions: (state) =>
      state.careUnits.map((u) => ({
        label: u.unit_name,
        value: u.care_unit_id,
        code: u.unit_code,
      })),

    /** Bed options formatted for dropdown use. */
    bedOptions: (state) =>
      state.beds.map((b) => ({
        label: b.bed_number,
        value: b.bed_id,
        type: b.bed_type,
        status: b.bed_status,
      })),

    /** Patient options formatted for dropdown use (child name select). */
    patientOptions: (state) =>
      state.patients.map((p) => {
        const name = `${p.first_name} ${p.last_name}`.trim();
        return {
          label: p.mrn ? `${name} (${p.mrn})` : name,
          value: p.patient_id,
          patientId: p.patient_id,
          mrn: p.mrn,
          dob: p.date_of_birth ?? '',
        };
      }),

    /** Whether to use mock reference data instead of real API. */
    useMockData: (): boolean => config.features.enableMockData,
  },

  actions: {
    /** Fetch all allergens. Uses mock data when VITE_ENABLE_MOCK=true. */
    async loadAllergens() {
      this.isLoadingAllergens = true;
      this.error = null;
      try {
        if (this.useMockData) {
          this.allergens = MOCK_ALLERGENS;
          return;
        }
        const { data } = await fetchAllergens();
        this.allergens = data ?? [];
      } catch (err) {
        if (handleSilent404(err, 'ReferenceStore:allergens')) {
          this.allergens = [];
        } else {
          this.error = getApiErrorMessage(err, 'apiErrors.failedLoadAllergens');
        }
      } finally {
        this.isLoadingAllergens = false;
      }
    },

    /** Fetch allergy severity codes. Uses mock data when VITE_ENABLE_MOCK=true. */
    async loadAllergySeverities() {
      this.isLoadingAllergySeverities = true;
      this.error = null;
      try {
        if (this.useMockData) {
          this.allergySeverities = MOCK_SEVERITIES;
          return;
        }
        const { data } = await fetchAllergySeverities();
        this.allergySeverities = data ?? [];
      } catch (err) {
        if (handleSilent404(err, 'ReferenceStore:allergySeverities')) {
          this.allergySeverities = [];
        } else {
          this.error = getApiErrorMessage(err, 'apiErrors.failedLoadAllergySeverities');
        }
      } finally {
        this.isLoadingAllergySeverities = false;
      }
    },

    /** Fetch facilities. Calls GET /api/v1/locations */
    async loadFacilities() {
      this.isLoadingFacilities = true;
      this.error = null;
      try {
        if (this.useMockData) {
          this.facilities = MOCK_FACILITIES;
          return;
        }
        const { data } = await fetchLocations();
        this.facilities = data ?? [];
      } catch (err) {
        if (handleSilent404(err, 'ReferenceStore:facilities')) {
          this.facilities = [];
        } else {
          this.error = getApiErrorMessage(err, 'apiErrors.failedLoadFacilities');
        }
      } finally {
        this.isLoadingFacilities = false;
      }
    },

    /** Fetch care units for a facility. Calls GET /api/v1/careUnits?facilityId= */
    async loadCareUnits(facilityId: string) {
      this.isLoadingCareUnits = true;
      this.error = null;
      try {
        if (this.useMockData) {
          this.careUnits = MOCK_CARE_UNITS[facilityId] ?? [];
          for (const u of this.careUnits) {
            this.careUnitNameMap[u.care_unit_id] = u.unit_name;
          }
          return;
        }
        const { data } = await fetchCareUnits(facilityId);
        this.careUnits = data ?? [];
        // Accumulate name map for location resolution
        for (const u of this.careUnits) {
          this.careUnitNameMap[u.care_unit_id] = u.unit_name;
        }
      } catch (err) {
        if (handleSilent404(err, 'ReferenceStore:careUnits')) {
          this.careUnits = [];
        } else {
          this.error = getApiErrorMessage(err, 'apiErrors.failedLoadCareUnits');
        }
      } finally {
        this.isLoadingCareUnits = false;
      }
    },

    /** Fetch beds for a care unit. Calls GET /api/v1/beds/:unit_id */
    async loadBeds(unitId: string) {
      this.isLoadingBeds = true;
      this.error = null;
      try {
        if (this.useMockData) {
          this.beds = MOCK_BEDS[unitId] ?? [];
          for (const b of this.beds) {
            this.bedLabelMap[b.bed_id] = b.bed_number;
          }
          return;
        }
        const { data } = await fetchBeds(unitId);
        this.beds = data ?? [];
        // Accumulate name map for location resolution
        for (const b of this.beds) {
          this.bedLabelMap[b.bed_id] = b.bed_number;
        }
      } catch (err) {
        if (handleSilent404(err, 'ReferenceStore:beds')) {
          this.beds = [];
        } else {
          this.error = getApiErrorMessage(err, 'apiErrors.failedLoadBeds');
        }
      } finally {
        this.isLoadingBeds = false;
      }
    },

    /** Load allergens + severities in parallel (common for admission forms). */
    async loadAllergyReferenceData() {
      await Promise.all([this.loadAllergens(), this.loadAllergySeverities()]);
    },

    /** Clear all cached reference data. */
    resetAll() {
      this.allergens = [];
      this.allergySeverities = [];
      this.facilities = [];
      this.careUnits = [];
      this.beds = [];
      this.patients = [];
      this.careUnitNameMap = {};
      this.bedLabelMap = {};
      this.error = null;
    },

    /**
     * Resolve a human-readable location string from care-unit / bed UUIDs.
     * Uses the accumulated name maps built by loadCareUnits / loadBeds.
     * Returns empty string if the IDs have not been resolved yet.
     */
    resolveLocation(careUnitId: string, bedId: string): string {
      const unit = this.careUnitNameMap[careUnitId] || '';
      const bed = this.bedLabelMap[bedId] || '';
      if (unit && bed) return `${unit} ${bed}`;
      if (unit) return unit;
      if (bed) return bed;
      return '';
    },

    /** Fetch patients for child name select. Calls GET /api/v1/patients */
    async loadPatients() {
      this.isLoadingPatients = true;
      this.error = null;
      try {
        const { data: response } = await fetchPatients({});
        const listItems: ApiPatientListItem[] = Array.isArray(response)
          ? response
          : (response as { data?: ApiPatientListItem[] }).data ?? [];
        this.patients = listItems;
      } catch (err) {
        if (handleSilent404(err, 'ReferenceStore:patients')) {
          this.patients = [];
        } else {
          this.error = getApiErrorMessage(err, 'apiErrors.failedFetchPatients');
        }
      } finally {
        this.isLoadingPatients = false;
      }
    },
  },
});
